/* eslint-disable @typescript-eslint/no-explicit-any */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AxiosInstance } from 'axios';
import { fetchPortPerformanceAPI } from 'kontrolEdge/components/Port/requests/request';

interface BandwidthItem {
  time: string;
  value: number;
}

interface BandwidthUtilization {
  inbound: BandwidthItem[];
  outbound: BandwidthItem[];
  timeRange: string;
  interval: string;
  unit: string;
}

export interface PortPerformanceData {
  portId: number;
  bandwidthUtilization: BandwidthUtilization;
}

// Extend the state to include new properties
interface PortState {
  lag: any;
  performanceData: PortPerformanceData | null;
  performanceLoading: boolean;
  performanceError: string | null;
  timeRange: string;
  interval: string;
}

const initialState: PortState = {
  lag: null,
  performanceData: null,
  performanceLoading: false,
  performanceError: null,
  timeRange: "5m",
  interval: "5s",
};

const portSlice = createSlice({
  name: 'port',
  initialState,
  reducers: {
    setLag: (state, action: PayloadAction<any>) => {
      state.lag = action.payload;
    },
    clearLag: (state) => {
      state.lag = null;
    },
    // Reducers for updating filters
    setTimeRange: (state, action: PayloadAction<string>) => {
      state.timeRange = action.payload;
    },
    setInterval: (state, action: PayloadAction<string>) => {
      state.interval = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPortPerformance.pending, (state) => {
        state.performanceLoading = true;
        state.performanceError = null;
      })
      .addCase(fetchPortPerformance.fulfilled, (state, action: PayloadAction<PortPerformanceData>) => {
        state.performanceData = action.payload;
        state.performanceLoading = false;
      })
      .addCase(fetchPortPerformance.rejected, (state, action) => {
        state.performanceLoading = false;
        state.performanceError = action.payload as string;
      });
    }
});

export const fetchPortPerformance = createAsyncThunk<
  PortPerformanceData,
  { api: AxiosInstance; portId: string, timeRange: string; interval: string, tenantId: number },
  { rejectValue: string }
>(
  'port/fetchPerformance',
  async ({ api, portId, timeRange, interval, tenantId }, { rejectWithValue }) => {
    try {
      const response = await fetchPortPerformanceAPI(api, portId, timeRange, interval, tenantId);
      return response.data.data as PortPerformanceData;
    } catch (err) {
      console.error("Error fetching performance data", err);
      return rejectWithValue("Failed to fetch performance data");
    }
  }
);

export const { setLag, clearLag, setInterval, setTimeRange } = portSlice.actions;
export default portSlice.reducer;
